import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { Template } from "../../model/accounting/template/Template.js";
import { TemplateValue } from "../../model/accounting/template/TemplateValue.js";
import { renderTemplate } from "../../model/accounting/template/renderTemplate.js";
import { NewEntry } from "../../model/accounting/entry/NewEntry.js";
import { useHref } from "../../../packages/router/href/HrefProvider.js";
import { TemplateValueError } from "../../model/accounting/template/TemplateValueError.js";
import { validateTemplate } from "../../model/accounting/template/validateTemplate.js";
import { saveEntry, uploadEntryFiles } from "../accounting/accountingApi.js";

type TemplateState = {
  template: Template;
  values: TemplateValue[];
  setValue: (name: string, value: string) => void;
  entry?: NewEntry;
  submitEntry: () => Promise<void>;
  setFiles: (files: FileList) => void;
  errors: TemplateValueError[];
};

const TemplateContext = createContext<TemplateState | undefined>(undefined);

type Props = {
  children: ReactNode;
  template: Template;
};

/**
 * Single template view state provider.
 */
export const TemplateProvider = ({ children, template }: Props) => {
  const { href, setHref } = useHref();

  const [values, setValues] = useState<TemplateValue[]>(() => {
    const parameters = Array.from(new URL(href).searchParams.entries());

    const values: TemplateValue[] = [];
    for (const { name } of template.variables) {
      const parameter = parameters.find((parameter) => parameter[0] === name);
      if (parameter) {
        // Prioritizes URL parameters.
        values.push({ name, value: parameter[1] });
      } else {
        if (template.initialValues) {
          // Otherwise tries to use an initial value.
          const initialValue = template.initialValues.find((initialValue) => initialValue.name === name);
          if (initialValue) {
            values.push({ name, value: initialValue.value });
          }
        }
      }
    }
    return values;
  });

  const [errors, setErrors] = useState<TemplateValueError[]>([]);
  const [files, setFiles] = useState<FileList>();

  const setValue = useCallback((name: string, value: string) => {
    setValues((values) => {
      const hasValue = values.some((item) => item.name === name);
      if (hasValue) {
        return values.map((item) => (item.name === name ? { name, value } : item));
      } else {
        return [...values, { name, value }];
      }
    });
  }, []);

  const entry = useMemo(() => {
    const result = renderTemplate(template, values);
    if (result.type === "success") {
      return result.entry;
    }
  }, [template, values]);

  const submitEntry = useCallback(async () => {
    const errors = validateTemplate(template, values);
    setErrors(errors);

    if (entry) {
      if (errors.length === 0) {
        const entryId = await saveEntry(entry);
        // Uploads files if there were any.
        if (files) {
          const data = new FormData();
          for (let i = 0; i < files.length; i++) {
            data.append("files", files[i]);
          }
          await uploadEntryFiles(entryId, data);
        }
        setFiles(undefined);
        setHref(`/entry/${entryId}`);
      }
    }
  }, [entry, files, template, setHref, values]);

  const value: TemplateState = {
    template,
    values,
    setValue,
    entry,
    submitEntry,
    setFiles,
    errors
  };

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>;
};

/**
 * Helper hook to access the simple template view state.
 */
export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error("The single template context is not provided.");
  }
  return context;
};
